/**
 * WidgetPreview - Preview live des widgets
 * 
 * Affiche un aperçu en temps réel du widget avec les données réelles.
 * Gère les états de chargement et d'erreur.
 */

import React from "react";
import { Typography, Loading } from "@/components/ui";
import { FaExclamationTriangle } from "react-icons/fa";
import { useWidgetData } from "@/hooks/use-widget-data";
import { FiltersContext } from "@/models/filter";
import type { WidgetConfig } from "@/models/widget-v2";
import { WIDGET_REGISTRY, widgetRequiresData } from "@/models/widget-v2";
import WidgetRenderer from "./WidgetRenderer";

// ============================================================================
// Types
// ============================================================================

interface WidgetPreviewProps {
  config: WidgetConfig;
  filtersCtx?: FiltersContext;
  className?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

const LoadingState: React.FC = () => (
  <div className="h-full w-full flex flex-col items-center justify-center text-content-tertiary">
    <Loading size={40} />
    <Typography variant="text-sm" className="mt-4">
      Chargement des données...
    </Typography>
  </div>
);

const ErrorState: React.FC<{ error: Error | null }> = ({ error }) => (
  <div className="h-full w-full flex flex-col items-center justify-center text-error">
    <FaExclamationTriangle size={32} className="mb-4 opacity-50" />
    <Typography variant="text-sm" className="font-medium mb-2">
      Erreur de chargement
    </Typography>
    <Typography variant="text-xs" className="text-content-tertiary text-center max-w-xs">
      {error?.message || "Une erreur est survenue lors du chargement des données."}
    </Typography>
  </div>
);

const EmptyState: React.FC<{ widgetType: string }> = ({ widgetType }) => {
  const meta = WIDGET_REGISTRY[widgetType as keyof typeof WIDGET_REGISTRY];
  
  return (
    <div className="h-full w-full flex flex-col items-center justify-center text-content-tertiary">
      <div className="text-4xl mb-4 opacity-50">
        {meta?.icon || "📊"}
      </div>
      <Typography variant="text-sm" className="font-medium mb-2">
        Aucune donnée
      </Typography>
      <Typography variant="text-xs" className="text-center max-w-xs">
        Configurez les sources de données pour afficher un aperçu.
      </Typography>
    </div>
  );
};

const NoDataNeededState: React.FC<{ widgetType: string }> = ({ widgetType }) => {
  const meta = WIDGET_REGISTRY[widgetType as keyof typeof WIDGET_REGISTRY];
  
  return (
    <div className="h-full w-full flex flex-col items-center justify-center text-content-tertiary">
      <div className="text-4xl mb-4">
        {meta?.icon || "📝"}
      </div>
      <Typography variant="text-sm" className="font-medium mb-2">
        {meta?.name || "Widget"}
      </Typography>
      <Typography variant="text-xs" className="text-center max-w-xs">
        Ce widget n'a pas besoin de données externes.
      </Typography>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  config,
  filtersCtx,
  className = "",
}) => {
  const requiresData = widgetRequiresData(config.type);
  
  // Les trackers sont optionnels - ils servent juste de filtre ES
  // Récupérer les données si le widget en a besoin
  const { data, isLoading, isError, error } = useWidgetData({
    config,
    filters: filtersCtx?.filtersData,
    enabled: requiresData,
  });
  
  // Widget statique (text)
  if (!requiresData) {
    return (
      <div className={`h-full w-full bg-arc-surface rounded-lg overflow-hidden ${className}`}>
        <WidgetRenderer config={config} data={null} filtersCtx={filtersCtx} />
      </div>
    );
  }
  
  // État de chargement
  if (isLoading) {
    return (
      <div className={`h-full w-full bg-arc-surface rounded-lg overflow-hidden ${className}`}>
        <LoadingState />
      </div>
    );
  }
  
  // État d'erreur
  if (isError) {
    return (
      <div className={`h-full w-full bg-arc-surface rounded-lg overflow-hidden ${className}`}>
        <ErrorState error={error} />
      </div>
    );
  }
  
  // Pas de données
  if (!data) {
    return (
      <div className={`h-full w-full bg-arc-surface rounded-lg overflow-hidden ${className}`}>
        <EmptyState widgetType={config.type} />
      </div>
    );
  }
  
  // Rendu du widget avec les données
  return (
    <div className={`h-full w-full bg-arc-surface rounded-lg overflow-hidden ${className}`}>
      <WidgetRenderer config={config} data={data} filtersCtx={filtersCtx} />
    </div>
  );
};

export default WidgetPreview;
